/**
 * pricing.js
 *
 * Shared pricing utilities for the catalog product system.
 *
 * Price semantics in this project:
 *   The price in a pricing tier represents the TOTAL price for that quantity.
 *   Example: tier { quantity: 500, price: 700 } means 500 cards cost ₹700 total.
 *   It does NOT mean ₹700 per card.
 *
 * unitPrice = total price for the selected quantity tier (not per-card)
 * perPiecePrice = unitPrice / quantity (for display only)
 */

/**
 * Resolve the applicable price (total for that qty) from a pricing tiers array.
 *
 * Rules:
 *   - Sort tiers ascending by quantity.
 *   - If qty < first tier's quantity, use first tier's price.
 *   - Walk tiers: if qty >= tier.quantity, use that tier's price.
 *   - Returns null if tiers is empty/null.
 *
 * @param {Array<{quantity: number, price: number|string}>} tiers
 * @param {number} qty
 * @returns {number|null}
 */
export function resolvePrice(tiers, qty) {
  if (!tiers || tiers.length === 0) return null;
  const sorted = [...tiers].sort((a, b) => a.quantity - b.quantity);
  if (qty < sorted[0].quantity) return parseFloat(sorted[0].price);
  let price = parseFloat(sorted[0].price);
  for (const t of sorted) {
    if (qty >= t.quantity) price = parseFloat(t.price);
    else break;
  }
  return price;
}

/**
 * Clamp a quantity value to the nearest valid value:
 *   >= minimumQuantity, aligned to quantityIncrement.
 *
 * @param {number} val
 * @param {number} min
 * @param {number} inc
 * @returns {number}
 */
export function clampQty(val, min, inc) {
  if (val < min) return min;
  const steps = Math.round((val - min) / inc);
  return min + steps * inc;
}

/**
 * Format a number as Indian Rupees.
 * Example: 1234.5 → "₹1,235"
 *
 * @param {number} amount
 * @param {boolean} showDecimal
 * @returns {string}
 */
export function formatINR(amount, showDecimal = false) {
  if (amount == null || isNaN(amount)) return '—';
  return '₹' + parseFloat(amount).toLocaleString('en-IN', {
    minimumFractionDigits: showDecimal ? 2 : 0,
    maximumFractionDigits: showDecimal ? 2 : 0,
  });
}
